<template>
  <div class="survey-container">
    <div class="survey-wrapper">
      <div class="survey-header">
        <img
          class="logo"
          src="@website-assets/img/logo-stowy.png"
          alt="Stowy Logo"
          @click="goHome"
        />
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
        </div>
        <p class="step-indicator">
          {{ $t('exclusiveOffer.step-of', { current: currentStepNumber, total: totalSteps }) }}
        </p>
      </div>

      <div class="survey-content">
        <!-- Introduction -->
        <div v-if="currentStep === 'intro'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.survey-intro-title') }}</h2>
          <p class="step-description">{{ $t('exclusiveOffer.survey-intro-desc') }}</p>
        </div>

        <!-- Step 1: Current Usage -->
        <div v-if="currentStep === 'step1'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step1-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q1-title') }}</label>
            <div class="radio-options">
              <label v-for="i in 4" :key="'q1-' + i" class="radio-option">
                <input type="radio" :value="i" v-model="answers.q1" />
                <span>{{ $t('exclusiveOffer.q1-opt' + i) }}</span>
              </label>
            </div>
          </div>

          <div class="question-block" v-if="answers.q1 && answers.q1 !== 4">
            <label class="question-label">{{ $t('exclusiveOffer.q2-title') }}</label>
            <div class="radio-options">
              <label v-for="i in 4" :key="'q2-' + i" class="radio-option">
                <input type="radio" :value="i" v-model="answers.q2" />
                <span>{{ $t('exclusiveOffer.q2-opt' + i) }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Step 2A: Non-users -->
        <div v-if="currentStep === 'step2a'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step2a-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q3-title') }}</label>
            <div class="checkbox-options">
              <label v-for="i in 9" :key="'q3-' + i" class="checkbox-option">
                <input type="checkbox" :value="i" v-model="answers.q3" />
                <span>{{ $t('exclusiveOffer.q3-opt' + i) }}</span>
              </label>
            </div>
            <input
              v-if="answers.q3.includes(9)"
              type="text"
              class="text-input"
              v-model="answers.q3_other"
              :placeholder="$t('exclusiveOffer.q3-other-placeholder')"
            />
          </div>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q4-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q4"
              :placeholder="$t('exclusiveOffer.q4-placeholder')"
              rows="4"
            ></textarea>
          </div>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q5-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q5"
              :placeholder="$t('exclusiveOffer.q5-placeholder')"
              rows="4"
            ></textarea>
          </div>
        </div>

        <!-- Step 2B: Active users -->
        <div v-if="currentStep === 'step2b'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step2b-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q6-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q6"
              :placeholder="$t('exclusiveOffer.q6-placeholder')"
              rows="4"
            ></textarea>
          </div>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q7-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q7"
              :placeholder="$t('exclusiveOffer.q7-placeholder')"
              rows="4"
            ></textarea>
          </div>
        </div>

        <!-- Step 3: Subscription -->
        <div v-if="currentStep === 'step3'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step3-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q8-title') }}</label>
            <div class="radio-options">
              <label v-for="i in 3" :key="'q8-' + i" class="radio-option">
                <input type="radio" :value="i" v-model="answers.q8" />
                <span>{{ $t('exclusiveOffer.q8-opt' + i) }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Step 4: Subscription barriers -->
        <div v-if="currentStep === 'step4'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step4-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q9-title') }}</label>
            <div class="checkbox-options">
              <label v-for="i in 6" :key="'q9-' + i" class="checkbox-option">
                <input type="checkbox" :value="i" v-model="answers.q9" />
                <span>{{ $t('exclusiveOffer.q9-opt' + i) }}</span>
              </label>
            </div>
            <input
              v-if="answers.q9.includes(6)"
              type="text"
              class="text-input"
              v-model="answers.q9_other"
              :placeholder="$t('exclusiveOffer.q9-other-placeholder')"
            />
          </div>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q10-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q10"
              :placeholder="$t('exclusiveOffer.q10-placeholder')"
              rows="4"
            ></textarea>
          </div>
        </div>

        <!-- Step 5: Final thoughts -->
        <div v-if="currentStep === 'step5'" class="step-section">
          <h2 class="step-title">{{ $t('exclusiveOffer.step5-title') }}</h2>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q11-title') }}</label>
            <textarea
              class="textarea-input"
              v-model="answers.q11"
              :placeholder="$t('exclusiveOffer.q11-placeholder')"
              rows="4"
            ></textarea>
          </div>

          <div class="question-block">
            <label class="question-label">{{ $t('exclusiveOffer.q12-title') }}</label>
            <div class="radio-options">
              <label v-for="i in 2" :key="'q12-' + i" class="radio-option">
                <input type="radio" :value="i" v-model="answers.q12" />
                <span>{{ $t('exclusiveOffer.q12-opt' + i) }}</span>
              </label>
            </div>
            <input
              v-if="answers.q12 === 1"
              type="email"
              class="text-input"
              v-model="answers.q12_email"
              :placeholder="$t('exclusiveOffer.q12-email-placeholder')"
            />
          </div>
        </div>
      </div>

      <div class="survey-navigation">
        <button
          v-if="currentStep !== 'intro'"
          class="btn btn-secondary"
          @click="previousStep"
        >
          {{ $t('exclusiveOffer.previous') }}
        </button>
        <button
          v-if="!isLastStep"
          class="btn btn-primary"
          @click="nextStep"
          :disabled="!canProceed"
        >
          {{ $t('exclusiveOffer.next') }}
        </button>
        <button
          v-if="isLastStep"
          class="btn btn-primary"
          @click="submitSurvey"
          :disabled="submitting"
        >
          {{ submitting ? 'En cours...' : $t('exclusiveOffer.submit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@common/firebase';

export default {
  name: 'SurveyForm',
  data() {
    return {
      currentStep: 'intro',
      stepHistory: ['intro'],
      answers: {
        q1: null,
        q2: null,
        q3: [],        // Changé en array pour choix multiples
        q3_other: '',
        q4: '',
        q5: '',
        q6: '',
        q7: '',
        q8: null,
        q9: [],        // Changé en array pour choix multiples
        q9_other: '',
        q10: '',
        q11: '',
        q12: null,
        q12_email: ''
      },
      submitting: false
    };
  },
  computed: {
    totalSteps() {
      // intro + step1 + (step2a or step2b) + step3 + step4? + step5
      return 5;
    },
    currentStepNumber() {
      const stepOrder = ['intro', 'step1', 'step2a', 'step2b', 'step3', 'step4', 'step5'];
      const index = stepOrder.indexOf(this.currentStep);
      if (index === -1) return 1;
      if (index === 0) return 1; // intro
      if (index === 1) return 2; // step1
      if (index === 2 || index === 3) return 3; // step2a or step2b
      if (index === 4) return 4; // step3
      if (index === 5) return 4; // step4
      if (index === 6) return 5; // step5
      return 1;
    },
    progressPercentage() {
      return (this.currentStepNumber / this.totalSteps) * 100;
    },
    isLastStep() {
      return this.currentStep === 'step5';
    },
    canProceed() {
      if (this.currentStep === 'intro') return true;

      if (this.currentStep === 'step1') {
        // Q1 always required
        if (!this.answers.q1) return false;
        // Q2 only required if Q1 !== 4 (if user has used the app)
        if (this.answers.q1 !== 4 && !this.answers.q2) return false;
        return true;
      }

      if (this.currentStep === 'step2a') {
        // Q3 requires at least 1 checkbox selected
        if (this.answers.q3.length === 0) return false;
        // If "Autre" (9) is checked, q3_other must be filled
        if (this.answers.q3.includes(9) && !this.answers.q3_other.trim()) return false;
        return true;
      }

      if (this.currentStep === 'step2b') return true; // Optional fields
      if (this.currentStep === 'step3') return this.answers.q8;

      if (this.currentStep === 'step4') {
        // Q9 requires at least 1 checkbox selected
        if (this.answers.q9.length === 0) return false;
        // If "Autre" (6) is checked, q9_other must be filled
        if (this.answers.q9.includes(6) && !this.answers.q9_other.trim()) return false;
        return true;
      }

      if (this.currentStep === 'step5') return true; // Optional fields
      return false;
    }
  },
  methods: {
    goHome() {
      this.$router.push('/');
    },
    nextStep() {
      let nextStep = '';

      if (this.currentStep === 'intro') {
        nextStep = 'step1';
      } else if (this.currentStep === 'step1') {
        // If Q1 === 4 (never used), go directly to step2a
        if (this.answers.q1 === 4) {
          nextStep = 'step2a';
        } else {
          // Otherwise, check Q2: if NOT 1 (not "very often"), go to step2a, else step2b
          nextStep = this.answers.q2 !== 1 ? 'step2a' : 'step2b';
        }
      } else if (this.currentStep === 'step2a' || this.currentStep === 'step2b') {
        nextStep = 'step3';
      } else if (this.currentStep === 'step3') {
        // Logic: if q8 is 1 (yes but didn't) OR 3 (no never), go to step4, else step5
        nextStep = (this.answers.q8 === 1 || this.answers.q8 === 3) ? 'step4' : 'step5';
      } else if (this.currentStep === 'step4') {
        nextStep = 'step5';
      }

      if (nextStep) {
        this.stepHistory.push(this.currentStep);
        this.currentStep = nextStep;
        this.scrollToTop();
      }
    },
    previousStep() {
      if (this.stepHistory.length > 0) {
        this.currentStep = this.stepHistory.pop();
        this.scrollToTop();
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    getVerboseAnswers() {
      // Mapping français pour toutes les questions et réponses
      const questionMappings = {
        q1: {
          question: "Quand avez-vous utilisé Stowy pour la dernière fois ?",
          options: {
            1: "Aujourd'hui / cette semaine",
            2: "Il y a quelques semaines",
            3: "Il y a plusieurs mois",
            4: "Je n'ai jamais vraiment utilisé l'application"
          }
        },
        q2: {
          question: "À quelle fréquence avez-vous lu une histoire avec votre/vos enfant(s) sur Stowy ?",
          options: {
            1: "Très souvent",
            2: "De temps en temps",
            3: "Une seule fois",
            4: "Jamais"
          }
        },
        q3: {
          question: "Quelle est la raison principale pour laquelle vous n'utilisez pas (ou plus) Stowy ?",
          options: {
            1: "Je n'en voyais pas l'utilité",
            2: "J'ai oublié que je l'avais installée",
            3: "J'ai rencontré des bugs",
            4: "Je n'ai pas trouvé comment m'abonner ou accéder aux histoires",
            5: "Le prix est trop élevé",
            6: "L'offre gratuite me suffit",
            7: "Je voulais juste tester par curiosité",
            8: "J'essaie de ne pas trop exposer mes enfants aux écrans",
            9: "Autre"
          }
        },
        q4: {
          question: "Si vous avez rencontré un problème ou un bug, pouvez-vous préciser lequel ?"
        },
        q5: {
          question: "Qu'est-ce qui vous donnerait envie de réutiliser Stowy ?"
        },
        q6: {
          question: "Qu'est-ce que vous aimez le plus dans Stowy ?"
        },
        q7: {
          question: "Et qu'est-ce qui pourrait être amélioré ?"
        },
        q8: {
          question: "Avez-vous envisagé de vous abonner ?",
          options: {
            1: "Oui, mais je ne l'ai pas fait",
            2: "Oui, je suis abonné",
            3: "Non, jamais"
          }
        },
        q9: {
          question: "Qu'est-ce qui vous a freiné ?",
          options: {
            1: "Le prix",
            2: "Je ne comprenais pas bien ce que l'abonnement apportait",
            3: "Je ne savais pas comment m'abonner",
            4: "L'offre gratuite me suffit",
            5: "Je préfère un autre modèle (achat à l'unité, offre familiale, etc.)",
            6: "Autre"
          }
        },
        q10: {
          question: "Si Stowy proposait un changement, qu'est-ce qui vous ferait sauter le pas ?"
        },
        q11: {
          question: "En une phrase : que devrait devenir Stowy pour que vous l'utilisiez plus souvent ?"
        },
        q12: {
          question: "(Optionnel) Souhaitez-vous qu'on vous tienne informé des améliorations à venir ?",
          options: {
            1: "Oui, par e-mail",
            2: "Non, merci"
          }
        }
      };

      const questions = [];

      // Process each answer
      Object.keys(this.answers).forEach(key => {
        const value = this.answers[key];

        // Skip empty values and "other" fields (will be included with main question)
        if (value === null || value === '' || key.includes('_')) return;

        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0) return;

        const mapping = questionMappings[key];
        if (!mapping) return;

        const questionObj = {
          id: key,
          question: mapping.question
        };

        // Handle different answer types
        if (Array.isArray(value)) {
          // Checkbox answers (Q3, Q9)
          questionObj.answers = value.map(val => {
            if (val === 9 && key === 'q3' && this.answers.q3_other) {
              return `Autre: ${this.answers.q3_other}`;
            }
            if (val === 6 && key === 'q9' && this.answers.q9_other) {
              return `Autre: ${this.answers.q9_other}`;
            }
            return mapping.options[val];
          });
        } else if (typeof value === 'number' && mapping.options) {
          // Radio answers with options (Q1, Q2, Q8, Q12)
          questionObj.answer = mapping.options[value];
        } else if (typeof value === 'string') {
          // Text answers (Q4, Q5, Q6, Q7, Q10, Q11, Q12_email)
          questionObj.answer = value;
        }

        questions.push(questionObj);
      });

      // Add email if provided
      if (this.answers.q12 === 1 && this.answers.q12_email) {
        questions.push({
          id: 'q12_email',
          question: "Adresse e-mail",
          answer: this.answers.q12_email
        });
      }

      return questions;
    },

    async submitSurvey() {
      this.submitting = true;

      try {
        // Prepare verbose survey data
        const surveyData = {
          questions: this.getVerboseAnswers(),
          metadata: {
            submittedAt: new Date(),
            locale: this.$i18n.locale,
            userAgent: navigator.userAgent
          }
        };

        // Save to Firestore
        await db.collection('surveySubmissions').add(surveyData);

        // Redirect to thank you page
        this.$router.push('/offre-exclusive/merci');
      } catch (error) {
        console.error('Error submitting survey:', error);
        alert('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../style/vars/index";

.survey-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%);
  padding: 2rem 1rem;
}

.survey-wrapper {
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 30px;
  box-shadow: 0px 10px 40px rgba(77, 160, 255, 0.15);
  padding: min(3rem, 5vw);
}

.survey-header {
  text-align: center;
  margin-bottom: 3rem;

  .logo {
    width: min(150px, 30vw);
    margin-bottom: 2rem;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 1rem;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #4DA0FF 0%, #7B4FD4 100%);
      transition: width 0.5s ease;
    }
  }

  .step-indicator {
    font-family: "Baloo 2", sans-serif;
    color: #666;
    font-size: 0.9rem;
  }
}

.survey-content {
  margin-bottom: 2rem;
}

.step-section {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-title {
  font-family: "Baloo 2", sans-serif;
  font-size: min(2rem, 6vw);
  font-weight: 700;
  color: #4da0ff;
  margin-bottom: 1.5rem;
  text-align: center;
}

.step-description {
  font-family: "Baloo 2", sans-serif;
  font-size: min(1.2rem, 4vw);
  color: #666;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.question-block {
  margin-bottom: 2.5rem;

  .question-label {
    display: block;
    font-family: "Baloo 2", sans-serif;
    font-size: min(1.2rem, 4vw);
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
  }
}

.radio-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .radio-option {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: #f8f9ff;
    border-radius: 15px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #e9ecff;
      transform: translateX(5px);
    }

    input[type="radio"] {
      margin-right: 1rem;
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    span {
      font-family: "Baloo 2", sans-serif;
      font-size: min(1rem, 3.5vw);
      color: #333;
    }

    input[type="radio"]:checked + span {
      font-weight: 600;
      color: #4da0ff;
    }
  }
}

.checkbox-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .checkbox-option {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: #f8f9ff;
    border-radius: 15px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #e9ecff;
      transform: translateX(5px);
    }

    input[type="checkbox"] {
      margin-right: 1rem;
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    span {
      font-family: "Baloo 2", sans-serif;
      font-size: min(1rem, 3.5vw);
      color: #333;
    }

    input[type="checkbox"]:checked + span {
      font-weight: 600;
      color: #4da0ff;
    }
  }
}

.text-input,
.textarea-input {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 15px;
  font-family: "Baloo 2", sans-serif;
  font-size: 1rem;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:focus {
    outline: none;
    border-color: #4da0ff;
    box-shadow: 0px 5px 15px rgba(77, 160, 255, 0.2);
  }
}

.textarea-input {
  resize: vertical;
  min-height: 100px;
}

.survey-navigation {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 2rem;
  border-top: 2px solid #e9ecef;

  .btn {
    flex: 1;
    max-width: 200px;
    font-size: min(1.3rem, 4.5vw);
    padding: 1rem 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.4;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (min-width: 768px) {
    .btn {
      font-size: 1.3rem;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .btn {
      max-width: 100%;
    }
  }
}
</style>
